// src/api/eventCompanies.js
import { collection, getDocs, doc, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

// Récupérer toutes les sociétés d'événementiel
export const fetchEventCompanies = async (filters = {}) => {
  try {
    let q = collection(db, 'eventCompanies');
    
    // Appliquer les filtres si nécessaire
    if (filters.city) {
      q = query(q, where('location', '>=', filters.city), where('location', '<=', filters.city + '\uf8ff'));
    }
    
    if (filters.specialty) {
      q = query(q, where('specialties', 'array-contains', filters.specialty));
    }
    
    if (filters.verified !== undefined) {
      q = query(q, where('verified', '==', filters.verified));
    }
    
    // Trier par rating décroissant par défaut
    q = query(q, orderBy('rating', 'desc'));
    
    // Limiter les résultats si spécifié
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erreur lors de la récupération des sociétés d\'événementiel:', error);
    throw error;
  }
};

// Récupérer une société d'événementiel spécifique
export const fetchEventCompanyById = async (companyId) => {
  try {
    const docRef = doc(db, 'eventCompanies', companyId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Société d\'événementiel non trouvée');
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la société:', error);
    throw error;
  }
};

// Récupérer les sociétés d'événementiel recommandées (les mieux notées)
export const fetchFeaturedEventCompanies = async (count = 6) => {
  try {
    const q = query(
      collection(db, 'eventCompanies'),
      where('verified', '==', true),
      orderBy('rating', 'desc'),
      limit(count)
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erreur lors de la récupération des sociétés recommandées:', error);
    // Fallback sans le filtre verified si l'index n'existe pas encore
    try {
      const fallbackQ = query(
        collection(db, 'eventCompanies'),
        orderBy('rating', 'desc'),
        limit(count)
      );
      
      const fallbackSnapshot = await getDocs(fallbackQ);
      return fallbackSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (fallbackError) {
      console.error('Erreur fallback:', fallbackError);
      return [];
    }
  }
};

// Données d'exemple pour le développement (à supprimer en production)
export const mockEventCompanies = [
  {
    id: '1',
    name: 'Elite Events Maroc',
    description: 'Spécialistes des événements haut de gamme au Maroc. Nous créons des expériences inoubliables pour vos mariages, galas et événements corporatifs.',
    logo: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=200&h=200&fit=crop&crop=center',
    coverImage: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&h=200&fit=crop',
    location: 'Casablanca',
    rating: 4.8,
    reviewCount: 127,
    specialties: ['Mariages', 'Événements corporatifs', 'Galas', 'Soirées privées'],
    priceRange: '15 000 MAD',
    verified: true,
    eventCount: 250,
    email: 'contact@eliteevents.ma',
    phone: '+212 522 123 456',
    website: 'https://eliteevents.ma'
  },
  {
    id: '2',
    name: 'Maroccan Magic Events',
    description: 'Créateurs d\'événements authentiquement marocains. Nous mélangeons tradition et modernité pour des célébrations uniques.',
    logo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=center',
    coverImage: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400&h=200&fit=crop',
    location: 'Marrakech',
    rating: 4.6,
    reviewCount: 89,
    specialties: ['Mariages traditionnels', 'Henné', 'Événements culturels', 'Fêtes berbères'],
    priceRange: '8 000 MAD',
    verified: true,
    eventCount: 180,
    email: 'info@maroccanmagic.com',
    phone: '+212 524 789 012'
  },
  {
    id: '3',
    name: 'Atlas Productions',
    description: 'Production complète d\'événements avec équipe technique professionnelle. Son, éclairage, vidéo et animation.',
    logo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=center',
    coverImage: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=200&fit=crop',
    location: 'Rabat',
    rating: 4.7,
    reviewCount: 203,
    specialties: ['Production technique', 'Concerts', 'Festivals', 'Conférences'],
    priceRange: '20 000 MAD',
    verified: true,
    eventCount: 320,
    email: 'booking@atlasproductions.ma',
    phone: '+212 537 654 321'
  },
  {
    id: '4',
    name: 'Luxury Weddings Morocco',
    description: 'Organisateurs de mariages de luxe avec une attention particulière aux détails. Votre jour J sera parfait.',
    logo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=center',
    coverImage: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400&h=200&fit=crop',
    location: 'Fès',
    rating: 4.9,
    reviewCount: 156,
    specialties: ['Mariages de luxe', 'Décoration florale', 'Traiteur gastronomique'],
    priceRange: '25 000 MAD',
    verified: true,
    eventCount: 95,
    email: 'contact@luxuryweddings.ma',
    phone: '+212 535 987 654'
  },
  {
    id: '5',
    name: 'Corporate Events Pro',
    description: 'Experts en événements d\'entreprise. Séminaires, lancements produit, team building et galas corporatifs.',
    logo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=center',
    coverImage: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=200&fit=crop',
    location: 'Tanger',
    rating: 4.5,
    reviewCount: 78,
    specialties: ['Événements corporatifs', 'Séminaires', 'Team building', 'Lancements produit'],
    priceRange: '12 000 MAD',
    verified: false,
    eventCount: 140,
    email: 'info@corporateevents.ma',
    phone: '+212 539 456 789'
  },
  {
    id: '6',
    name: 'Sahara Events',
    description: 'Événements dans le désert et locations exceptionnelles. Vivez une expérience unique sous les étoiles.',
    logo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=center',
    coverImage: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=400&h=200&fit=crop',
    location: 'Ouarzazate',
    rating: 4.4,
    reviewCount: 42,
    specialties: ['Événements désert', 'Camping de luxe', 'Mariages berbères', 'Excursions'],
    priceRange: '18 000 MAD',
    verified: true,
    eventCount: 65,
    email: 'contact@saharaevents.com',
    phone: '+212 524 123 789'
  }
];

// Fonction pour obtenir les données mockées (développement)
export const getMockEventCompanies = (count = 6) => {
  return Promise.resolve(mockEventCompanies.slice(0, count));
};